import updateUserConfigurationPont from '../actions/actionCreators/updateUserConfigurationPont';
import loc, { format } from 'owa-localize';
import { userMailInteractionAction } from 'owa-mail-actions';
import deleteConversationStoreUpdate from 'owa-mail-actions/lib/triage/deleteConversationStoreUpdate';
import { IgnoreConversationsConfirmDialog } from 'owa-mail-ignore-view';
import {
    MailRowDataPropertyGetter,
    isConversationView,
    getRowIdsFromRowKeys,
} from 'owa-mail-list-store';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { processApplyConversationActionResponseMessage } from 'owa-mail-triage-action-response-processor';
import PontType from 'owa-service/lib/contract/PontType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { orchestrator } from 'satcheljs';
import {
    ignore_ignoreDialogTitle,
    ignore_ignoreDialogTitleNItems,
    ignore_stopIgnoringDialogTitle,
    ignore_stopIgnoringDialogTitleNItems,
    ignore_ignoreDialogSubtext,
    ignore_stopIgnoringDialogSubtext,
} from './toggleIgnoreConversationsOrchestrator.locstring.json';
import toggleIgnoreConversations, {
    internalToggleIgnoreConversations,
} from '../actions/toggleIgnoreConversations';
import deleteItemsStoreUpdate from 'owa-mail-actions/lib/triage/deleteItemsStoreUpdate';
import createItemContextsForDeleteStoreUpdate from '../actions/helpers/createItemContextsForDeleteStoreUpdate';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getApolloClient } from 'owa-apollo';
import {
    IgnoreConversationDocument,
    IgnoreConversationMutation,
} from '../graphql/__generated__/ignoreConversationMutation.interface';

type IgnoreConversationResult = IgnoreConversationMutation['ignoreConversation'];

export default orchestrator(toggleIgnoreConversations, actionMessage => {
    const shouldIgnore = actionMessage.shouldIgnore;
    const rowKeysToIgnore = actionMessage.rowKeysToIgnore;

    const pontType: PontType = shouldIgnore
        ? PontType.IgnoreConversation
        : PontType.CancelIgnoreConversation;

    // show dialog if pont is set
    if ((pontType & getUserConfiguration().UserOptions.NewEnabledPonts) == pontType) {
        let dialogTitle: string;

        if (shouldIgnore) {
            dialogTitle =
                rowKeysToIgnore.length == 1
                    ? loc(ignore_ignoreDialogTitle)
                    : format(loc(ignore_ignoreDialogTitleNItems), rowKeysToIgnore.length);
        } else {
            dialogTitle =
                rowKeysToIgnore.length == 1
                    ? loc(ignore_stopIgnoringDialogTitle)
                    : format(loc(ignore_stopIgnoringDialogTitleNItems), rowKeysToIgnore.length);
        }
        const dialogText = shouldIgnore
            ? loc(ignore_ignoreDialogSubtext)
            : loc(ignore_stopIgnoringDialogSubtext);

        const dialog = new IgnoreConversationsConfirmDialog(
            dialogTitle,
            dialogText,
            checked => {
                if (checked) {
                    updateUserConfigurationPont(pontType);
                }
                internalToggleIgnoreConversations(
                    actionMessage.rowKeysToIgnore,
                    actionMessage.tableView,
                    shouldIgnore,
                    actionMessage.actionSource
                );
            },
            () => {} /* onDismiss */
        );
        dialog.show();
    } else {
        // pont already set, toggle ignore without dialog
        internalToggleIgnoreConversations(
            actionMessage.rowKeysToIgnore,
            actionMessage.tableView,
            shouldIgnore,
            actionMessage.actionSource
        );
    }
});

export const internalToggleIgnoreConversationsFn = orchestrator(
    internalToggleIgnoreConversations,
    actionMessage => {
        const { shouldIgnore, tableView, rowKeysToIgnore } = actionMessage;
        const conversationIds: string[] = [];
        const tableViewId = tableView.id;

        for (const rowKey of rowKeysToIgnore) {
            conversationIds.push(MailRowDataPropertyGetter.getConversationId(rowKey, tableView));
        }

        if (shouldIgnore) {
            const instrumentationContexts = getInstrumentationContextsFromTableView(
                rowKeysToIgnore,
                tableView
            );
            userMailInteractionAction('Ignore', instrumentationContexts, {
                rowKeys: rowKeysToIgnore,
            });
        }

        // clean up items client side
        if (isConversationView(tableView)) {
            deleteConversationStoreUpdate(conversationIds, tableView, 'Ignore');
        } else {
            const itemIds = getRowIdsFromRowKeys(rowKeysToIgnore, tableViewId);
            const itemContexts = createItemContextsForDeleteStoreUpdate(itemIds);
            deleteItemsStoreUpdate(itemContexts, tableView.tableQuery.folderId, 'Ignore');
        }

        const ignoreConversationPromise = exportedHelperFunctions.ignoreConversationMutation(
            conversationIds,
            tableView.tableQuery.folderId,
            shouldIgnore,
            isFeatureEnabled('tri-processIgnoreRightAway')
        );

        return processApplyConversationActionResponseMessage(
            ignoreConversationPromise,
            rowKeysToIgnore,
            tableView.tableQuery.folderId,
            tableView,
            /*
             * Ignore operation is performed upon delay server-side and not immediately, hence we get
             * row modified notifications while each item in the conversation is getting moved to deleted items folder
             * Hence we do not want to remove this entry from tombstone when action completes, rather we will wait
             * for the rowDelete notification and remove it then which guarantees that the row has been completely
             * moved to deleted items folder
             */
            false /* shouldRemoveRowsFromTombstone */,
            true /*skipCheckingResponseMessage*/
        );
    }
);

/**
 * The mutation for ignore conversation.
 * @param conversationIds the conversationIds that need to be ignoreed
 * @param contextFolderId  the folder where the conversations will be ignored from
 * @param enableAlwaysDelete should ignore
 * @param processRightAway to be processed right awat, right now this is controlled by a flight.
 */
const ignoreConversationMutationFn = async function ignoreConversationMutation(
    conversationIds: string[],
    contextFolderId: string,
    enableAlwaysDelete: boolean,
    processRightAway: boolean
): Promise<IgnoreConversationResult | undefined> {
    const conversationSnapShots = conversationIds.map(conversationId => {
        return {
            id: conversationId,
        };
    });

    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            conversations: conversationSnapShots,
            folderId: contextFolderId,
            enableAlwaysDelete: enableAlwaysDelete,
            processRightAway: processRightAway,
        },
        mutation: IgnoreConversationDocument,
    });

    return result?.data?.ignoreConversation;
};

/**
 * This helper method is exported so it can be mocked for unit testing
 */
export const exportedHelperFunctions = {
    ignoreConversationMutation: ignoreConversationMutationFn,
};
